import { atom, getDefaultStore } from "jotai";
import { isLoadingSuggestionStateAtom } from "./states";
import {
  canvasAtom,
  edgesAtom,
  nodesAtom,
  emitAiPredictionOutputEventAtom,
  zslCreatorBaseUrlAtom,
} from "../index";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { createHash } from "crypto";

export const fetchRecommendationAtom = atom(
  null,
  async (
    get,
    set,
    { sourceNodeId, nodeId }: { sourceNodeId: string; nodeId: string },
  ) => {
    const store = getDefaultStore();
    const controller = new AbortController();
    set(isLoadingSuggestionStateAtom, true);
    const streamLog: any[] = [];
    const zslCreatorBaseUrl = get(zslCreatorBaseUrlAtom);

    try {
      const nodes = get(nodesAtom);
      const edges = get(edgesAtom);
      const canvas = get(canvasAtom);
      const predictionInput = { nodes, edges, canvas, sourceNodeId };

      await fetchEventSource(`/api/zsl/canvas/recommend_asset`, {
        method: "POST",
        headers: {
          "z-override-base-url": zslCreatorBaseUrl,
        },
        body: JSON.stringify(predictionInput),
        openWhenHidden: true,
        signal: controller.signal,
        onmessage(msg) {
          streamLog.push(msg.data);
          const data = JSON.parse(msg.data);
          // Update node with recommendation
          if (data.node_type && data.suggestion) {
            set(nodesAtom, (prev) =>
              prev.map((node) => {
                if (node.id === nodeId) {
                  return {
                    ...node,
                    data: {
                      ...node.data,
                      label: data.suggestion,
                      suggestedType: data.node_type.toLowerCase(),
                      error: false,
                    },
                  };
                }
                return node;
              }),
            );
          }

          // Update ZSL data
          if (data.zsl) {
            set(nodesAtom, (prev) =>
              prev.map((node) => {
                if (node.id === nodeId) {
                  return {
                    ...node,
                    data: {
                      ...node.data,
                      zsl: data.zsl,
                    },
                  };
                }
                return node;
              }),
            );

            store.set(emitAiPredictionOutputEventAtom, {
              prediction_id: createHash("sha256")
                .update(JSON.stringify(data.zsl))
                .digest("hex"),
              correlation_id: get(canvasAtom).id,
              prediction_source: "canvas.next_step",
              prediction_json_input: JSON.stringify(predictionInput),
              prediction_json_output: JSON.stringify(data.zsl || {}),
            });
          }
        },
        onerror(err) {
          console.error("EventSource error:", err);
          throw err;
        },
      });
    } catch (error: unknown) {
      if (error instanceof Error && error.name === "AbortError") {
        return;
      }

      set(nodesAtom, (prev) =>
        prev.map((node) => {
          if (node.id === nodeId) {
            return {
              ...node,
              data: {
                ...node.data,
                error: true,
                label: "Error loading suggestion",
              },
            };
          }
          return node;
        }),
      );
      console.error("Fetch recommendation error:", error);
    } finally {
      set(isLoadingSuggestionStateAtom, false);
      controller.abort();
    }
  },
);

fetchRecommendationAtom.debugLabel = "fetchRecommendationAtom";
